import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { MoreOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps } from 'antd';
import dayjs from 'dayjs';
import { MyApplicationStatusTag } from 'widgets/my-applications-list/ui/MyApplicationStatusTag/MyApplicationStatusTag';

import { useApplicationUserQuery } from 'entities/application/api';
import { useFindAppById } from 'entities/application/modal/useFindAppById';
import { useManageApplication } from 'entities/application/modal/useManageApplication';
import { useFindSubscriptionsByAppId } from 'entities/subscription/model/useFindSubscriptionsByAppId';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import {
	AppCardContainerStyled,
	AppCardDescriptionStyled,
	AppCardLogoStyled,
	AppCardNameStyled,
	AppCardTopStyled,
	ContentContainerStyled,
	DropdownButtonContainerStyled,
	StatusesContainerStyled,
	SubscriptionStatusStyled,
} from './MyApplicationsItem.styles';

interface AppCardProps {
	applicationId: string;
}

export const MyApplicationItem: FC<AppCardProps> = ({ applicationId }) => {
	const { t } = useTranslation();
	const application = useFindAppById(applicationId);

	const subscriptions = useFindSubscriptionsByAppId(applicationId);

	const authorization = useAuthorization();
	const canPurchaseApps = authorization?.canPurchaseApps;

	const { installApplication, deleteApplication } = useManageApplication(applicationId);
	const [isInstalled, setIsInstalled] = useState(false);

	const { data: applicationUserInfo } = useApplicationUserQuery(applicationId);

	const history = useHistory();

	useEffect(() => {
		setIsInstalled(applicationUserInfo?.status === `Installed`);
	}, [applicationUserInfo]);

	if (!application || !applicationUserInfo) {
		return null;
	}

	const stateToPass = { prevLocation: `my-applications` };

	const navigateToApp = (): void => {
		history.push(`/${application.id}`, stateToPass);
	};

	const handleMenuClick: MenuProps['onClick'] = e => {
		// eslint-disable-next-line default-case
		switch (e.key) {
			case `1`:
				void installApplication();
				setIsInstalled(true);
				break;
			case `3`:
				void deleteApplication();
				setIsInstalled(false);
				break;
		}
	};

	const allMenuItems = [
		{
			label: t(`Marketplace_Install_App`),
			key: `1`,
			included: !isInstalled,
		},

		{
			// FIXME
			label: <a href='/my?tab=subscriptions'>{t(`Marketplace_Manage_Subscription`)}</a>,
			key: `2`,
			included: canPurchaseApps && !!subscriptions.length,
		},
		{
			label: t(`Marketplace_Delete_App`),
			key: `3`,
			included: isInstalled,
			danger: true,
		},
	];

	const items: MenuProps['items'] = allMenuItems
		.filter(n => n.included)
		.map(n => ({
			key: n.key,
			label: n.label,
			danger: n.danger,
		}));

	const menuProps = {
		items,
		onClick: handleMenuClick,
	};

	return (
		<AppCardContainerStyled>
			<ContentContainerStyled>
				<AppCardTopStyled onClick={navigateToApp}>
					<AppCardLogoStyled src={application.logoUrl} />
					<AppCardNameStyled>{application.name}</AppCardNameStyled>
				</AppCardTopStyled>
				<AppCardDescriptionStyled>
					{application.descriptionCard ?? application.description}
				</AppCardDescriptionStyled>
				<DropdownButtonContainerStyled>
					<Dropdown menu={menuProps} placement='bottomRight'>
						<Button style={{ padding: `0` }} icon={<MoreOutlined />} size='small' />
					</Dropdown>
				</DropdownButtonContainerStyled>
			</ContentContainerStyled>
			<StatusesContainerStyled>
				<MyApplicationStatusTag isFree={!application.plans?.length} isInstalled={isInstalled} />
				{!!subscriptions.length && (
					<SubscriptionStatusStyled>
						{subscriptions[0].activeTrial
							? t(`Marketplace_Trial_Days`, {
									days: dayjs(subscriptions[0].trialEndsAt).diff(dayjs(), `days`),
							  })
							: t(`Marketplace_Subscription_Active`)}
					</SubscriptionStatusStyled>
				)}
			</StatusesContainerStyled>
		</AppCardContainerStyled>
	);
};
